import React, { FC, useRef, useEffect } from 'react'
import { useTranslation } from 'gatsby-plugin-react-i18next'
import { gsap } from 'gsap/all'
import { createShowFromBottomAnimation } from 'src/helpers/animations'
import SectionHeader from 'src/components/ui/SectionHeader'
import LogoMarquee from 'src/components/ui/LogoMarquee'
import clientImageIds from './list-of-images'

export interface ITrustedByTranslationContent {
  sectionName?: string
  subtitle?: string
  firstPartOfTitle?: string
  secondPartOfTitle?: string
  thirdPartOfTitle?: string
  content?: string
}

interface ITrustedByProps {
  className?: string
  translationContent?: ITrustedByTranslationContent
}

const TrustedBy: FC<ITrustedByProps> = ({ className = '', translationContent }) => {
  const { t } = useTranslation('main-page')
  const marqueesRef = useRef<HTMLDivElement>(null)
  const timeline = useRef<any>(null)

  const subtitle = translationContent?.subtitle || t('trusted us')
  const firstPartOfTitle = translationContent?.firstPartOfTitle || t('trusted by')
  const secondPartOfTitle = translationContent?.secondPartOfTitle || t('friends and brands')
  const thirdPartOfTitle = translationContent?.thirdPartOfTitle
  const description = translationContent?.content || t('trusted by content')

  useEffect(() => {
    if (!timeline.current) {
      timeline.current = gsap.timeline()
    }
    if (marqueesRef.current) {
      createShowFromBottomAnimation(timeline.current, marqueesRef.current)
    }
  }, [])

  // Split images into two rows for the marquee
  const half = Math.ceil(clientImageIds.length / 2)
  const firstRow = clientImageIds.slice(0, half)
  const secondRow = clientImageIds.slice(half)

  return (
    <section
      id="trusted-by"
      className={`w-full py-20 md:py-32 flex flex-col overflow-hidden bg-transparent ${className}`}
    >
      {/* Header */}
      <div className="container mx-auto mb-8 md:mb-16">
        <SectionHeader
          subtitle={subtitle}
          titlePart1={firstPartOfTitle}
          titlePart2={secondPartOfTitle}
          titlePart3={thirdPartOfTitle}
          description={description}
        />
      </div>

      {/* Marquees spanning full width */}
      <div ref={marqueesRef} className="w-full space-y-0 opacity-0">
        <LogoMarquee
          imageIds={firstRow}
          baseVelocity={-1}
        />
        <LogoMarquee
          imageIds={secondRow}
          baseVelocity={1}
        />
      </div>
    </section>
  )
}

export default TrustedBy
